import { Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import { LoadState } from '../types/LoadState';
import {
  deleteForteckningar,
  listForteckningar,
  retryCreateForteckning,
} from './ForteckningarClient';
import { ForteckningarList } from './ForteckningarList';
import { Forteckning } from './types/Forteckning';

interface ForteckningarListContainerProps {
  createdId: string;
}

export const ForteckningarListContainer: React.FC<
  ForteckningarListContainerProps
> = ({ createdId }) => {
  const maxPollTime = 3600000; // 1 hour
  const pollInterval = 5000;
  const [savedForteckningar, setSavedForteckningar] = useState<Forteckning[]>(
    []
  );
  const [isPolling, setIsPolling] = useState(false);
  const [pollingError, setPollingError] = useState('');
  const [deleteLoadState, setDeleteLoadState] = useState(LoadState.Idle);
  const [retryLoadState, setRetryLoadState] = useState(LoadState.Idle);
  const [timeoutId, setTimeoutId] = useState<number>(undefined);

  useEffect(() => startPolling(), [createdId]);

  const startPolling = (forceRequest = false): void => {
    if (isPolling && !forceRequest) {
      return;
    }
    setIsPolling(true);

    const fetchForteckningslista = async (pollCount: number): Promise<void> => {
      if (pollCount * pollInterval > maxPollTime) {
        setIsPolling(false);
        return setPollingError('Nedladdning av förteckning tog för lång tid.');
      }
      const result = await listForteckningar();
      if (result.success) {
        const forteckningar = result.data;
        setSavedForteckningar(forteckningar);
        if (
          !forteckningar.find(
            (akt) => akt.status === 'REGISTERED' || akt.status === 'PRODUCING'
          )
        ) {
          setPollingError('');
          setIsPolling(false);
        } else {
          setTimeoutId(
            window.setTimeout(
              () => fetchForteckningslista(++pollCount),
              pollInterval
            )
          );
        }
      } else {
        setPollingError(
          result.statusCode === 401 || result.statusCode === 403
            ? 'Du har blivit utloggad. Ladda om sidan och logga in igen.'
            : 'Oväntat fel när sparade förteckningar skulle hämtas.'
        );
        if (result.statusCode >= 500) {
          setTimeoutId(
            window.setTimeout(
              () => fetchForteckningslista(++pollCount),
              pollInterval
            )
          );
        } else {
          setIsPolling(false);
        }
      }
    };

    timeoutId && window.clearTimeout(timeoutId);
    fetchForteckningslista(0);
  };

  const handleDelete = async (forteckningar: Forteckning[]) => {
    setDeleteLoadState(LoadState.Loading);
    const result = await deleteForteckningar(
      forteckningar.map((forteckning) => forteckning.id)
    );
    startPolling();
    setDeleteLoadState(result.success ? LoadState.Success : LoadState.Error);
    return result;
  };

  const handleRetry = async (forteckning: Forteckning) => {
    setRetryLoadState(LoadState.Loading);
    const result = await retryCreateForteckning(forteckning.id);
    startPolling(true);
    setRetryLoadState(result.success ? LoadState.Success : LoadState.Error);
  };

  return pollingError ? (
    <Alert severity="error" sx={{ mb: 2 }}>
      {pollingError}
    </Alert>
  ) : (
    <ForteckningarList
      savedForteckningar={savedForteckningar}
      handleDelete={handleDelete}
      handleRetry={handleRetry}
      deleteLoadState={deleteLoadState}
      retryLoadState={retryLoadState}
    />
  );
};
